/**
 * Encrypted Device Cache Service
 * Provides real encrypted caching functionality using AES-256-CBC
 */

import { deviceCache } from '../../utils/deviceCache'
import { encryptionService } from '../encryptionService'
import { cacheSettingsService } from './cacheSettingsService'

export interface CacheStats {
  totalRecords: number
  encryptedRecords: number
  userCounts: { [userEmail: string]: number }
  encryptionHealth: {
    isInitialized: boolean
    encryptionServiceAvailable: boolean
    storesAvailable: string[]
    totalEncryptedRecords: number
  }
}

class EncryptedDeviceCacheService {
  /**
   * Get real cache statistics from encrypted storage
   */
  async getCacheStats(): Promise<CacheStats> {
    // Return empty stats if cache disabled
    if (!cacheSettingsService.isCacheEnabled()) {
      return {
        totalRecords: 0,
        encryptedRecords: 0,
        userCounts: {},
        encryptionHealth: {
          isInitialized: false,
          encryptionServiceAvailable: encryptionService.isAvailable(),
          storesAvailable: [],
          totalEncryptedRecords: 0,
        },
      }
    }

    try {
      const stats = await deviceCache.getCacheStats()
      return stats
    } catch (error) {
      console.error('Failed to get cache stats:', error)
      return {
        totalRecords: 0,
        encryptedRecords: 0,
        userCounts: {},
        encryptionHealth: {
          isInitialized: false,
          encryptionServiceAvailable: encryptionService.isAvailable(),
          storesAvailable: [],
          totalEncryptedRecords: 0,
        },
      }
    }
  }

  /**
   * Clear encrypted cache for a specific user
   */
  async clearUserCache(userEmail: string): Promise<void> {
    try {
      console.log(`🗑️ Clearing encrypted cache for user: ${userEmail}`)
      await deviceCache.clearDevices(userEmail)
      console.log('✅ Encrypted cache cleared successfully')
    } catch (error) {
      console.error('Failed to clear encrypted cache:', error)
      throw error
    }
  }

  /**
   * Clear all encrypted cache data
   */
  async clearAllCache(): Promise<void> {
    try {
      console.log('🗑️ Clearing all encrypted cache')
      await deviceCache.clearAll()
      console.log('✅ All encrypted cache cleared successfully')
    } catch (error) {
      console.error('Failed to clear all encrypted cache:', error)
      throw error
    }
  }

  /**
   * Get user-specific cache statistics
   */
  async getUserCacheStats(userEmail: string): Promise<{
    hasCache: boolean
    deviceCount: number
    lastUpdated: number | null
  }> {
    // Return no-cache stats if disabled
    if (!cacheSettingsService.isCacheEnabled()) {
      return {
        hasCache: false,
        deviceCount: 0,
        lastUpdated: null,
      }
    }

    try {
      return await deviceCache.getUserCacheStats(userEmail)
    } catch (error) {
      console.error('Failed to get user cache stats:', error)
      return {
        hasCache: false,
        deviceCount: 0,
        lastUpdated: null,
      }
    }
  }

  /**
   * Get encryption information
   */
  getEncryptionInfo() {
    return encryptionService.getEncryptionStats()
  }

  /**
   * Check if encryption is working properly
   */
  async verifyEncryption(): Promise<boolean> {
    return encryptionService.isAvailable()
  }

  /**
   * Clean up old unencrypted database
   */
  async cleanupLegacyCache(): Promise<void> {
    try {
      console.log('🧹 Cleaning up legacy unencrypted cache')
      await deviceCache.cleanupOldDatabase()
      console.log('✅ Legacy cache cleaned up')
    } catch (error) {
      console.error('Failed to cleanup legacy cache:', error)
    }
  }
}

export const encryptedDeviceCacheService = new EncryptedDeviceCacheService()
